import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ProductImageWithFallbacks } from "./ProductImageWithFallbacks";
import { ProductPriceBlock } from "./ProductPriceBlock";
import { BtnContent } from "./BtnIcon";
import { IoCartOutline } from "react-icons/io5";

/**
 * Horizontal product row — scroll sideways; the centred card elevates (lifts).
 */
function ProductGiftTray({ currentProductId, products, addToCart }) {
  const scrollRef = useRef(null);
  const [elevatedIndex, setElevatedIndex] = useState(0);

  const list = useMemo(
    () =>
      (products || []).filter((p) => String(p.id) !== String(currentProductId)).slice(0, 18),
    [products, currentProductId],
  );

  const updateElevated = useCallback(() => {
    const viewport = scrollRef.current;
    if (!viewport) return;
    const cards = viewport.querySelectorAll(".product-gift-tray-card");
    if (!cards.length) return;

    const center = viewport.scrollLeft + viewport.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;

    cards.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const dist = Math.abs(cardCenter - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    });

    setElevatedIndex(best);
  }, []);

  useEffect(() => {
    const viewport = scrollRef.current;
    if (!viewport || !list.length) return;

    updateElevated();
    viewport.addEventListener("scroll", updateElevated, { passive: true });
    window.addEventListener("resize", updateElevated);

    const ro = new ResizeObserver(updateElevated);
    ro.observe(viewport);

    return () => {
      viewport.removeEventListener("scroll", updateElevated);
      window.removeEventListener("resize", updateElevated);
      ro.disconnect();
    };
  }, [list, updateElevated]);

  const handleAdd = (product, event) => {
    event.preventDefault();
    event.stopPropagation();
    addToCart(product);
    const btn = event.currentTarget;
    if (!btn) return;
    const prev = btn.textContent;
    btn.textContent = "✔ Added";
    btn.disabled = true;
    window.setTimeout(() => {
      btn.textContent = prev;
      btn.disabled = product.stock === 0;
    }, 1400);
  };

  if (!list.length) return null;

  return (
    <section className="product-gift-tray" aria-labelledby="product-gift-tray-heading">
      <div className="product-gift-tray__intro">
        <h2 id="product-gift-tray-heading" className="product-gift-tray__title">
          More gift boxes
        </h2>
        <p className="product-gift-tray__hint">
          Scroll sideways — the box in the centre lifts up so you can focus on it.
        </p>
      </div>

      <div className="product-gift-tray__rail product-gift-tray__rail--elevate">
        <div
          ref={scrollRef}
          className="product-gift-tray__scroll"
          tabIndex={0}
          aria-label="More products, scroll horizontally"
        >
          <div className="product-gift-tray__track">
            {list.map((p, i) => (
              <article
                className={`product-gift-tray-card${i === elevatedIndex ? " product-gift-tray-card--elevated" : ""}`}
                key={p.id}
              >
                <Link to={`/product/${p.id}`} state={{ product: p }} className="product-gift-tray-card__media">
                  <ProductImageWithFallbacks urls={p.images} alt={p.name} loading="lazy" />
                </Link>
                <div className="product-gift-tray-card__body">
                  <Link to={`/product/${p.id}`} state={{ product: p }}>
                    <h3>{p.name}</h3>
                  </Link>
                  <ProductPriceBlock product={p} variant="shop" />
                  <button
                    type="button"
                    className="btn"
                    onClick={(e) => handleAdd(p, e)}
                    disabled={p.stock === 0}
                  >
                    {p.stock === 0 ? (
                      "Out of stock"
                    ) : (
                      <BtnContent icon={IoCartOutline}>Add to cart</BtnContent>
                    )}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductGiftTray;
